import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type ParamValue = string | number | null | undefined;

interface UseUrlStateOptions {
  /** Replace history entry instead of pushing (default: true) */
  replace?: boolean;
}

/**
 * Custom hook for syncing state with URL search parameters.
 * Provides a way to read and write URL parameters while maintaining React state.
 */
export function useUrlState(options: UseUrlStateOptions = {}) {
  const { replace = true } = options;
  const location = useLocation();
  const navigate = useNavigate();

  // Use refs to keep setParam/setParams stable while still accessing current values
  const locationRef = useRef(location);
  locationRef.current = location;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  /**
   * Get a parameter value from the URL
   */
  const getParam = useCallback(
    (key: string): string | null => {
      return searchParams.get(key);
    },
    [searchParams],
  );

  /**
   * Get a parameter as a number, with optional default
   */
  const getNumberParam = useCallback(
    (key: string, defaultValue?: number): number | undefined => {
      const value = searchParams.get(key);
      if (value === null) return defaultValue;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    },
    [searchParams],
  );

  /**
   * Set a single parameter
   */
  const setParam = useCallback(
    (key: string, value: ParamValue) => {
      const currentLocation = locationRef.current;
      const newParams = new URLSearchParams(currentLocation.search);

      if (value === null || value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }

      navigate(
        {
          pathname: currentLocation.pathname,
          search: newParams.toString(),
        },
        { replace },
      );
    },
    [navigate, replace],
  );

  /**
   * Set multiple parameters at once
   */
  const setParams = useCallback(
    (params: Record<string, ParamValue>) => {
      const currentLocation = locationRef.current;
      const newParams = new URLSearchParams(currentLocation.search);

      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      navigate(
        {
          pathname: currentLocation.pathname,
          search: newParams.toString(),
        },
        { replace },
      );
    },
    [navigate, replace],
  );

  /**
   * Clear all parameters
   */
  const clearParams = useCallback(() => {
    const currentLocation = locationRef.current;
    navigate(
      {
        pathname: currentLocation.pathname,
        search: '',
      },
      { replace },
    );
  }, [navigate, replace]);

  /**
   * Clear specific parameters while keeping others
   */
  const clearSpecificParams = useCallback(
    (keys: string[]) => {
      const currentLocation = locationRef.current;
      const newParams = new URLSearchParams(currentLocation.search);
      keys.forEach((key) => newParams.delete(key));

      navigate(
        {
          pathname: currentLocation.pathname,
          search: newParams.toString(),
        },
        { replace },
      );
    },
    [navigate, replace],
  );

  return {
    getParam,
    getNumberParam,
    setParam,
    setParams,
    clearParams,
    clearSpecificParams,
    searchParams,
  };
}
